import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCspData, type CspSubscription } from '@/api/csp';
import { usePublicSetting } from '@/api/settings';
import { useCompanies } from '@/api/insights';
import { useScopeFilter } from '@/contexts/ClientScopeContext';

/**
 * CSP module helpers — CSV normalisation, renewal status, and the CSV / Outlook (.ics)
 * exports. Ported from NCEasy's app.js; behaviour is kept identical unless noted.
 */

// -------- Settings --------
//
// The expiring threshold and the long-expired cut-off are ORG-WIDE admin settings
// (Admin → CSP → Settings: `csp.expiringThresholdDays`, `csp.longExpiredDays`). Only the
// "Hide long-expired" view toggle is per user (localStorage).

const PREF_KEY = 'cosmos:csp:prefs';
interface UserPrefs { hideLongExpired: boolean }

function readUserPrefs(): UserPrefs {
  try { return { hideLongExpired: true, ...JSON.parse(localStorage.getItem(PREF_KEY) ?? '{}') }; } catch { return { hideLongExpired: true }; }
}

const listeners = new Set<(p: UserPrefs) => void>();

export interface CspPrefs {
  /** "Expiring soon" within this many days (admin setting). */
  threshold: number;
  /** Expired longer than this is "long-expired" (admin setting; Microsoft's recovery window). */
  longExpiredDays: number;
  /** Per-user view toggle. */
  hideLongExpired: boolean;
}

/** Admin thresholds + the user's "hide long-expired" toggle, shared live across every CSP page. */
export function useCspPrefs(): [CspPrefs, (patch: Partial<UserPrefs>) => void] {
  const threshold = usePublicSetting<number>('csp.expiringThresholdDays', 30);
  const longExpiredDays = usePublicSetting<number>('csp.longExpiredDays', 35);
  const [user, setUser] = useState(readUserPrefs);
  useEffect(() => { listeners.add(setUser); return () => { listeners.delete(setUser); }; }, []);
  const update = useCallback((patch: Partial<UserPrefs>) => {
    const next = { ...readUserPrefs(), ...patch };
    try { localStorage.setItem(PREF_KEY, JSON.stringify(next)); } catch { /* storage unavailable */ }
    listeners.forEach((l) => l(next));
  }, []);
  return [{ threshold, longExpiredDays, hideLongExpired: user.hideLongExpired }, update];
}

// -------- Rows with live status --------

export type CspStatus = 'active' | 'expiring' | 'expired' | 'unknown';

export interface CspRow extends CspSubscription {
  /** Stable key for React lists. */
  key: string;
  /** Days from today (local midnight) to the end date — stable through the day. */
  daysLeft: number | null;
  status: CspStatus;
}

/** "2026-10-13" → local-midnight Date (never UTC — that would shift a day in Melbourne). */
export function localDate(iso: string | null): Date | null {
  if (!iso) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
}

export function daysUntil(iso: string | null): number | null {
  const d = localDate(iso);
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86_400_000);
}

export function statusOf(daysLeft: number | null, threshold: number): CspStatus {
  if (daysLeft == null) return 'unknown';
  if (daysLeft < 0) return 'expired';
  if (daysLeft <= threshold) return 'expiring';
  return 'active';
}

export const isLongExpired = (r: CspRow, days: number) => r.daysLeft != null && r.daysLeft < -days;

/** The shared dataset with status computed against today and the user's threshold. */
/**
 * `scoped`: narrow to the header's client — the report customers the company index links to it
 * (csp_mapping + the same name auto-match as the Mappings page). Mappings/Upload stay unscoped.
 */
export function useCspRows(opts: { scoped?: boolean } = {}) {
  const data = useCspData();
  const [prefs, setPrefs] = useCspPrefs();
  const { companyId } = useScopeFilter();
  const scoping = !!opts.scoped && companyId != null;
  const companies = useCompanies({ enabled: scoping });
  const customers = useMemo(() => {
    if (!scoping) return null;
    return new Set(companies.data?.companies.find((c) => c.id === companyId)?.csp?.customers ?? []);
  }, [scoping, companies.data, companyId]);
  const all = useMemo<CspRow[]>(() => (data.data?.subscriptions ?? [])
    .filter((s) => !customers || customers.has(s.customer))
    .map((s, i) => {
      const daysLeft = daysUntil(s.endDate);
      return { ...s, key: `${i}:${s.customer}:${s.plan}:${s.endDateRaw}`, daysLeft, status: statusOf(daysLeft, prefs.threshold) };
    }), [data.data, prefs.threshold, customers]);
  const visible = useMemo(() => (prefs.hideLongExpired ? all.filter((r) => !isLongExpired(r, prefs.longExpiredDays)) : all), [all, prefs.hideLongExpired, prefs.longExpiredDays]);
  return { query: data, dataset: data.data ?? null, all, visible, hidden: all.length - visible.length, prefs, setPrefs };
}

export const STATUS_LABEL: Record<CspStatus, string> = { active: 'Active', expiring: 'Expiring soon', expired: 'Expired', unknown: 'Unknown' };
const STATUS_RANK: Record<CspStatus, number> = { expired: 0, expiring: 1, active: 2, unknown: 3 };
export const worstStatus = (rows: CspRow[]): CspStatus =>
  rows.reduce<CspStatus>((w, r) => (STATUS_RANK[r.status] < STATUS_RANK[w] ? r.status : w), 'unknown');

/** NCE seat reductions are only allowed within 7 days after the renewal / anniversary date. */
export const canReduce = (r: CspRow) => r.daysLeft != null && r.daysLeft <= 0 && r.daysLeft >= -7;

// -------- Sorting --------

export type CspSortKey = 'customer' | 'plan' | 'status' | 'quantity' | 'commitment' | 'autorenew' | 'days';
export interface CspSort { key: CspSortKey; dir: 'asc' | 'desc' }

export function sortRows(rows: CspRow[], s: CspSort): CspRow[] {
  const dir = s.dir === 'asc' ? 1 : -1;
  const val = (r: CspRow): string | number => {
    switch (s.key) {
      case 'customer': return r.customer.toLowerCase();
      case 'plan': return r.plan.toLowerCase();
      case 'status': return STATUS_RANK[r.status];
      case 'quantity': return r.quantity;
      case 'commitment': return r.commitment.toLowerCase();
      case 'autorenew': return r.autoRenew ? 0 : 1;
      case 'days': return r.daysLeft ?? 99_999;
    }
  };
  return [...rows].sort((a, b) => { const x = val(a), y = val(b); return (x < y ? -1 : x > y ? 1 : 0) * dir; });
}

// -------- CSV import (Partner Center "Microsoft Tenant Report") --------

/** "15/04/2026", "2026-04-15", or anything Date can parse → yyyy-mm-dd (local), else null. */
export function parseDateToIso(str: string): string | null {
  if (!str) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (y: number, m: number, d: number) => {
    const dt = new Date(y, m - 1, d);
    return Number.isNaN(dt.getTime()) ? null : `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  };
  let m = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(str); // DD/MM/YYYY (AU)
  if (m) return fmt(+m[3], +m[2], +m[1]);
  m = /^(\d{4})-(\d{1,2})-(\d{1,2})/.exec(str);
  if (m) return fmt(+m[1], +m[2], +m[3]);
  const d = new Date(str);
  return Number.isNaN(d.getTime()) ? null : fmt(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** One CSV row → a subscription. Each field accepts several header aliases (case-insensitive). */
export function normalizeRow(row: Record<string, string | undefined>): CspSubscription {
  const headers = Object.keys(row);
  const get = (keys: string[]) => {
    for (const k of keys) {
      const h = headers.find((x) => x.toLowerCase().trim() === k.toLowerCase());
      const v = h ? row[h]?.trim() : '';
      if (v) return v;
    }
    return '';
  };

  // "Customer Name (Account Manager)" → customer + account manager
  const customerRaw = get(['Customer', 'Customer Name', 'Company', 'Client']);
  const am = /^(.+?)\s*\(([^)]+)\)\s*$/.exec(customerRaw);
  // "Product Name (guid)" → "Product Name"
  const plan = get(['Plan', 'Product', 'Subscription', 'Offer Name', 'SKU Name']).replace(/\s*\([a-f0-9-]{36}\)\s*$/i, '').trim();
  // "Subscription Auto-Renew Date" is the next renewal / end-of-term date.
  const endDateRaw = get(['End Date', 'Expiry Date', 'Renewal Date', 'Term End Date', 'Subscription Auto-Renew Date',
    'Auto-Renew Date', 'Auto Renew Date', 'Subscription End Date', 'Commitment End Date']);
  const autoRenewRaw = get(['Auto Renew', 'AutoRenew', 'Auto RenewFlag', 'Auto Renew Flag', 'AutoRenewFlag', 'AutoRenewEnabled', 'Auto Renew Enabled']);

  return {
    customer: am ? am[1].trim() : customerRaw,
    accountManager: am ? am[2].trim() : '',
    plan,
    service: get(['Service']),
    commitment: get(['Commitment Term (Billing Frequency)', 'Commitment Term', 'Billing Frequency', 'Term']),
    quantity: parseInt(get(['Quantity', 'Seats', 'License Count', 'License Quantity', 'Current Subscription Quantity', 'Subscription Quantity']), 10) || 0,
    startDate: parseDateToIso(get(['Start Date', 'Subscription Start Date'])),
    endDate: parseDateToIso(endDateRaw),
    endDateRaw,
    autoRenew: ['on', 'yes', 'true'].includes(autoRenewRaw.toLowerCase()),
    autoRenewRaw,
  };
}

// -------- Formatting --------

export function fmtDate(iso: string | null): string {
  const d = localDate(iso) ?? (iso ? new Date(iso) : null);
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function money(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const todayStamp = () => new Date().toISOString().slice(0, 10);

function download(content: string, name: string, type: string) {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// -------- Exports --------

export function exportCsv(rows: CspRow[]) {
  if (!rows.length) return;
  const headers = ['Customer', 'Account Manager', 'Plan', 'Commitment Term', 'Quantity', 'Start Date', 'End Date', 'Auto Renew', 'Days Left', 'Status'];
  const cell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map((r) => [r.customer, r.accountManager, r.plan, r.commitment, r.quantity, fmtDate(r.startDate), fmtDate(r.endDate), r.autoRenewRaw, r.daysLeft ?? '', STATUS_LABEL[r.status]].map(cell).join(','));
  download([headers.map(cell).join(','), ...lines].join('\n'), `nce-expiries-${todayStamp()}.csv`, 'text/csv');
}

const icsEscape = (s: string) => (s || '').replace(/[,;\\]/g, (c) => `\\${c}`).replace(/\n/g, '\\n');
function hash(s: string) { let h = 0; for (let i = 0; i < s.length; i++) { h = ((h << 5) - h) + s.charCodeAt(i); h |= 0; } return Math.abs(h).toString(36); }

/** Outlook calendar of renewals — 1:00–1:15 pm Melbourne, reminders 14 and 7 days before. */
export function exportIcs(rows: CspRow[], customer?: string) {
  const events = rows.filter((r) => r.endDate).map((r) => {
    const date = r.endDate!.replace(/-/g, '');
    const description = [
      `Customer: ${r.customer}`, `Account Manager: ${r.accountManager}`, `Plan: ${r.plan}`, `Quantity: ${r.quantity}`,
      `Commitment: ${r.commitment}`, `Auto Renew: ${r.autoRenewRaw}`, `Expiry: ${fmtDate(r.endDate)}`, `Days Remaining: ${r.daysLeft}`,
    ].join('\\n');
    const alarm = (days: number) => ['BEGIN:VALARM', `TRIGGER:-P${days}D`, 'ACTION:DISPLAY',
      `DESCRIPTION:NCE subscription expiring in ${days} days: ${icsEscape(r.customer)} - ${icsEscape(r.plan)}`, 'END:VALARM'];
    return [
      'BEGIN:VEVENT',
      `UID:nce-${hash(r.customer + r.plan + r.endDateRaw)}@synfo.com.au`,
      `DTSTART;TZID=Australia/Melbourne:${date}T130000`,
      `DTEND;TZID=Australia/Melbourne:${date}T131500`,
      `SUMMARY:${icsEscape(`NCE Expiry: ${r.customer} — ${r.plan}`)}`,
      `DESCRIPTION:${icsEscape(description)}`,
      'CATEGORIES:NCE Renewal',
      'STATUS:CONFIRMED',
      ...alarm(14), ...alarm(7),
      'END:VEVENT',
    ].join('\r\n');
  });
  const ics = [
    'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Synfo//Cosmos CSP//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH',
    'X-WR-CALNAME:NCE Subscription Expiries', 'X-WR-TIMEZONE:Australia/Melbourne',
    'BEGIN:VTIMEZONE', 'TZID:Australia/Melbourne',
    'BEGIN:STANDARD', 'DTSTART:19700405T030000', 'RRULE:FREQ=YEARLY;BYDAY=1SU;BYMONTH=4', 'TZOFFSETFROM:+1100', 'TZOFFSETTO:+1000', 'TZNAME:AEST', 'END:STANDARD',
    'BEGIN:DAYLIGHT', 'DTSTART:19701004T020000', 'RRULE:FREQ=YEARLY;BYDAY=1SU;BYMONTH=10', 'TZOFFSETFROM:+1000', 'TZOFFSETTO:+1100', 'TZNAME:AEDT', 'END:DAYLIGHT',
    'END:VTIMEZONE',
    ...events,
    'END:VCALENDAR',
  ].join('\r\n');
  const name = customer ? `nce-${customer.replace(/[^a-zA-Z0-9]/g, '_').slice(0, 40)}-expiries-${todayStamp()}.ics` : `nce-all-expiries-${todayStamp()}.ics`;
  download(ics, name, 'text/calendar');
}

// -------- Name matching (mappings auto-suggest) --------

export function normName(s: string | null | undefined): string {
  return String(s || '').toLowerCase()
    .replace(/\b(pty|ltd|limited|inc|llc|co|the|group|holdings|au|australia)\b/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

export function termClass(s: string | null | undefined): 'monthly' | 'yearly' | '' {
  const t = String(s || '').toLowerCase();
  if (t.includes('month')) return 'monthly';
  if (t.includes('year') || t.includes('annual') || t.includes('12')) return 'yearly';
  return '';
}
